import * as R from 'ramda'
import * as urql from 'urql'
import * as React from 'react'
import * as ReactRouter from 'react-router-dom'
import PropTypes from 'prop-types'
import { AnimatePresence } from 'framer-motion'

import * as common from '@rushplay/common'
import { useI18n } from '@rushplay/i18n'

import * as icons from './icons'
import { CasinoGameTile } from './casino-game-tile'
import { GameQuery } from './game-query'
import { GameTileAnimation } from './game-tile-animation'
import { GameTileSection } from './game-tile-section'
import { LiveCasinoGameTile } from './live-casino-game-tile'
import { LiveCasinoQuery } from './live-casino-query'
import { mapIndexed } from './map-indexed'
import { useGameInfoUrlQuery } from './use-game-info-url-query'

const getGame = `
query Game($id: ID!) {
  game(id: $id) {
    category
    id
    key
  }
}
`

// TODO: Remove when BE have implemented similar games
function randomItems(list, limit, result = []) {
  if (result.length === limit) {
    return R.reject(R.isNil, result)
  }

  const randomInt = Math.floor(Math.random() * list.length)
  const nextList = R.without([list[randomInt]], list)

  return randomItems(nextList, limit, R.prepend(list[randomInt], result))
}

function Section(props) {
  const location = ReactRouter.useLocation()
  const Tile = props.tile

  return (
    <GameTileSection
      sectionTitle="game-info.similiar-games.title"
      size="small"
      layout={R.length(props.ids) <= 2 ? 'grid' : 'row'}
      columns={R.length(props.ids)}
    >
      {data => (
        <AnimatePresence>
          {mapIndexed(
            (id, index) => (
              <GameTileAnimation key={id} delay={(index % 6) * 0.04}>
                <ReactRouter.Link
                  key={`${id}-similar-games`}
                  to={`/casino/games/${id}${location.search}`}
                >
                  <Tile id={id} size="small" />
                </ReactRouter.Link>
              </GameTileAnimation>
            ),
            R.take(data.visibleGames, props.ids)
          )}
        </AnimatePresence>
      )}
    </GameTileSection>
  )
}

Section.propTypes = {
  ids: PropTypes.array,
  tile: PropTypes.func,
}

export function SimilarGamesSection() {
  const gameInfoQuery = useGameInfoUrlQuery()
  const { gameId } = ReactRouter.useParams()
  const [response] = urql.useQuery({
    query: getGame,
    variables: { id: gameId },
  })
  const i18n = useI18n()

  if (response.fetching) {
    return null
  }

  if (response.error) {
    return (
      <common.Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        fontSize={7}
        flexGrow="1"
        backgroundColor="failed-loading-casino-background"
        borderRadius={1}
        flexDirection="column"
        px={2}
      >
        <icons.Error />
        <common.Box
          pt={1}
          fontSize={0}
          textAlign="center"
          opacity="0.5"
          lineHeight="1.35"
        >
          {i18n.translate('errors.general.unknown')}
        </common.Box>
      </common.Box>
    )
  }

  const game = R.pathOr({}, ['game'], response.data)

  const isLiveCasino = R.equals('live-casino', gameInfoQuery.referrer)

  return isLiveCasino ? (
    <LiveCasinoQuery lobby>
      {({ ids }) => {
        if (ids.length === 0) {
          return (
            <common.Box
              display="flex"
              justifyContent="center"
              alignItems="self"
              fontSize={8}
              pt={5}
              flexGrow="1"
            >
              <icons.Spinner delay />
            </common.Box>
          )
        }

        const gameIds = randomItems(ids, 6)

        return <Section ids={gameIds} tile={LiveCasinoGameTile} />
      }}
    </LiveCasinoQuery>
  ) : (
    <React.Fragment>
      <GameQuery name="similar" params={{ related: [gameId], limit: 4 }}>
        {gq => {
          if (gq.isLoading) {
            return (
              <common.Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                fontSize={7}
                pt={5}
                flexGrow="1"
              >
                <icons.Spinner delay />
              </common.Box>
            )
          }

          if (R.isEmpty(gq.ids) && R.not(gq.isLoading)) {
            return (
              <GameQuery
                name="random"
                params={{ text: game.category, limit: 24 }}
              >
                {randomGq => {
                  if (randomGq.isLoading) {
                    return (
                      <common.Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        fontSize={7}
                        pt={5}
                        flexGrow="1"
                      >
                        <icons.Spinner delay />
                      </common.Box>
                    )
                  }

                  const gameIds = randomItems(randomGq.ids, 4)

                  return <Section ids={gameIds} tile={CasinoGameTile} />
                }}
              </GameQuery>
            )
          }
          return <Section ids={gq.ids} tile={CasinoGameTile} />
        }}
      </GameQuery>
    </React.Fragment>
  )
}

// default export for loadable
export default SimilarGamesSection
